import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, MapPin, CalendarDays, Car, ShieldCheck, Star, Phone, Pencil, X } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useRoleBase } from "../../hooks/useRoleBase";
import { Card, StatusPill, Spinner, EmptyState } from "../../components/dashboard/DashboardShell";
import OrderActions, { ReviewForm } from "../../components/order/OrderActions";
import ReportView from "../../components/order/ReportView";
import ChecklistForm from "../../components/order/ChecklistForm";
import { rupiah, fmtDate, fmtDateTime, PAYMENT_LABELS, STATUS_LABELS } from "../../lib/format";

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-9 h-9 rounded-xl bg-slate-50 text-slate-500 flex items-center justify-center shrink-0"><Icon size={16} /></span>
      <div className="min-w-0"><p className="text-[11px] font-semibold text-slate-400">{label}</p><p className="text-sm font-bold text-slate-800 break-words">{value}</p></div>
    </div>
  );
}

function PersonCard({ title, person, rating, onClick }) {
  return (
    <Card title={title}>
      <button onClick={onClick} disabled={!onClick} className="flex items-center gap-3 w-full text-left disabled:cursor-default">
        <img src={person?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(person?.name || "U")}&background=2563eb&color=fff`} alt={person?.name} className="w-12 h-12 rounded-full object-cover" />
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-900 truncate">{person?.name}</p>
          {rating != null && <p className="text-[11px] text-amber-500 font-bold flex items-center gap-1"><Star size={10} fill="#f59e0b" /> {rating} <span className="text-slate-400 font-medium">({person.rating_count} ulasan)</span></p>}
          {person?.phone && <p className="text-[11px] text-slate-400 flex items-center gap-1"><Phone size={10} /> {person.phone}</p>}
        </div>
      </button>
    </Card>
  );
}

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const base = useRoleBase();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: o, isLoading, isError } = useQuery({ queryKey: ["order", orderId], queryFn: () => api.get(`/orders/${orderId}`).then((r) => r.data) });
  const [editingReport, setEditingReport] = useState(false);

  if (isLoading) return <Spinner />;
  if (isError || !o) return <EmptyState title="Pesanan tidak ditemukan" action={<button onClick={() => navigate(`${base}/pesanan`)} className="text-sm font-bold text-blue-600">Kembali</button>} />;
  const v = o.vehicle_info || {};
  const canEditReport = user.role === "inspector" && o.status === "menunggu_review" && !!o.report;

  return (
    <div data-testid="order-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4" data-testid="back-btn"><ArrowLeft size={16} /> Kembali</button>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-extrabold text-slate-900" data-testid="order-detail-id">{o.order_id}</h1>
            <StatusPill status={o.status} />
          </div>
          <p className="text-sm text-slate-500 mt-1">Inspeksi {o.vehicle_type_label} · dibuat {fmtDateTime(o.created_at)}</p>
        </div>
        <div className="text-right">
          <p className="text-[11px] font-semibold text-slate-400">Total Pembayaran</p>
          <p className="text-2xl font-extrabold text-blue-600" data-testid="order-price">{rupiah(o.price)}</p>
        </div>
      </div>

      <div className="grid xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          <Card title="Detail Inspeksi">
            <div className="grid sm:grid-cols-2 gap-4">
              <InfoRow icon={Car} label="Kendaraan" value={`${o.vehicle_type_label}${v.brand ? ` · ${v.brand} ${v.model || ""} ${v.year || ""}` : ""}`} />
              <InfoRow icon={CalendarDays} label="Jadwal" value={`${fmtDate(o.scheduled_date, "EEEE, d MMMM yyyy")} · ${o.scheduled_time}`} />
              <InfoRow icon={MapPin} label="Lokasi" value={`${o.address}, ${o.city}`} />
              <InfoRow icon={ShieldCheck} label="Plat / Odometer" value={`${v.plate || "-"} · ${v.odometer ? v.odometer.toLocaleString("id-ID") + " km" : "-"}`} />
            </div>
            {o.notes && <p className="mt-4 text-xs text-slate-500 bg-slate-50 rounded-lg p-3"><b className="text-slate-700">Catatan pengguna:</b> {o.notes}</p>}
          </Card>

          {user.role === "inspector" && o.status === "sedang_inspeksi" && !o.has_report && (
            <Card title="Checklist Inspeksi" action={<span className="text-[11px] font-bold text-violet-600 bg-violet-50 rounded-full px-2.5 py-1">{o.vehicle_type_label}</span>}>
              <ChecklistForm order={o} />
            </Card>
          )}

          {o.report && (
            <Card title="Laporan Inspeksi" action={canEditReport && (
              <button onClick={() => setEditingReport((s) => !s)} data-testid="edit-report-btn"
                className="flex items-center gap-1.5 text-[11px] font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-full px-3 py-1.5 transition-colors">
                {editingReport ? <><X size={13} /> Batal</> : <><Pencil size={13} /> Edit Laporan</>}
              </button>
            )}>
              {editingReport
                ? <ChecklistForm order={o} report={o.report} onSubmitted={() => { setEditingReport(false); qc.invalidateQueries({ queryKey: ["order", orderId] }); }} />
                : <ReportView report={o.report} />}
            </Card>
          )}

          {o.status === "selesai" && (
            <Card title={o.review ? "Ulasan Pengguna" : "Beri Ulasan"}>
              {o.review ? (
                <div data-testid="review-display">
                  <p className="text-amber-400 text-sm">{"★".repeat(o.review.rating)}<span className="text-slate-200">{"★".repeat(5 - o.review.rating)}</span> <span className="text-xs text-slate-500 font-bold ml-1">{o.review.rating}/5</span></p>
                  <p className="text-sm text-slate-600 mt-2">{o.review.comment || <i className="text-slate-400">Tanpa komentar</i>}</p>
                </div>
              ) : user.role === "pengguna" ? (
                <ReviewForm order={o} onDone={() => qc.invalidateQueries({ queryKey: ["order", orderId] })} />
              ) : <p className="text-sm text-slate-400">Pengguna belum memberikan ulasan.</p>}
            </Card>
          )}

          <Card title="Riwayat Status">
            <div className="space-y-0" data-testid="order-timeline">
              {[...o.timeline].reverse().map((t, i) => (
                <div key={i} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <span className={`w-2.5 h-2.5 rounded-full mt-1.5 ${i === 0 ? "bg-blue-600 ring-4 ring-blue-100" : "bg-slate-300"}`} />
                    {i < o.timeline.length - 1 && <span className="w-px flex-1 bg-slate-200" />}
                  </div>
                  <div className="pb-5">
                    <p className="text-[13px] font-extrabold text-slate-900">{STATUS_LABELS[t.status] || t.status}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{t.note}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{fmtDateTime(t.at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card title="Aksi"><OrderActions order={o} role={user.role} base={base} /></Card>
          <Card title="Pembayaran">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Biaya inspeksi</span><span className="font-bold text-slate-800">{rupiah(o.price)}</span></div>
              {user.role !== "pengguna" && (
                <>
                  <div className="flex justify-between"><span className="text-slate-500">Komisi platform</span><span className="font-semibold text-slate-600">- {rupiah(o.platform_fee)}</span></div>
                  <div className="flex justify-between border-t border-slate-100 pt-2"><span className="text-slate-500">Pendapatan inspector</span><span className="font-extrabold text-emerald-600">{rupiah(o.inspector_earning)}</span></div>
                </>
              )}
              <div className="flex justify-between items-center border-t border-slate-100 pt-2">
                <span className="text-slate-500">Status dana</span>
                <span className="text-[11px] font-bold rounded-full px-2.5 py-1 bg-slate-100 text-slate-700" data-testid="payment-status">{PAYMENT_LABELS[o.payment_status] || o.payment_status}</span>
              </div>
              {o.payment?.method && <p className="text-[11px] text-slate-400">Metode: {o.payment.method === "mock" ? "Midtrans (simulasi)" : "Midtrans"} · {o.payment.paid_at ? fmtDateTime(o.payment.paid_at) : ""}</p>}
            </div>
          </Card>
          {user.role !== "inspector" && <PersonCard title="Inspector" person={o.inspector} rating={o.inspector?.rating_avg?.toFixed?.(1)} onClick={user.role === "pengguna" ? () => navigate(`/pengguna/inspector/${o.inspector_id}`) : null} />}
          {user.role !== "pengguna" && <PersonCard title="Pengguna" person={o.user} />}
        </div>
      </div>
    </div>
  );
}
